import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import { DetailBackButton, PageMeta, PageShell } from '@/shared';
import { t } from '@/i18n';
import NotFound from '@/pages/not-found/NotFound';

import type { FinancialCalendarEvent } from '@/entities/financial-calendar';
import type { CalendarFilter } from '@/features/financial-calendar/model/types';
import {
  CALENDAR_DAY_PRISTINE_IMPACTS,
  calendarDayFilter,
  calendarDayPath,
  isCalendarDayIndexable,
  isValidCalendarDay,
  shiftCalendarDay,
} from '@/features/financial-calendar/model/day-page';
import { useFinancialCalendarDailyCountsQuery } from '@/features/financial-calendar/model/query';
import { formatEventDateHeading } from '@/features/financial-calendar/model/utils';
import { EventList } from '@/features/financial-calendar/ui/EventList';
import { EventDetailPanel } from '@/features/financial-calendar/ui/EventDetailPanel';
import { EventDetailDrawer } from '@/features/financial-calendar/ui/EventDetailDrawer';
import { CalendarDisclaimer } from '@/features/financial-calendar/ui/CalendarDisclaimer';

/**
 * TRAD-3709 P1 — 금융캘린더 «일자별» 페이지 `/financial-calendar/YYYY-MM-DD` (팀장 승인 10/12 · 마케팅 4주 계획 ⑤).
 * 검색에 걸리는 고유 URL·title·description·canonical. prerender 는 최근 14일 + 앞 7일(prerender.mjs).
 *
 * 기준 화면 = 메인 캘린더 `index.tsx` 의 «목록 보기»(UI 룰 1 — 새 레이아웃 0):
 *   h1 같은 클래스 · 목록 2/3 + 상세 패널 1/3(데스크톱) · 모바일 상세는 EventDetailDrawer · 하단 면책 문구 같은 키.
 *   목록은 EventList 그대로(필터만 «그날 하루 · 전부»). 추가된 것은 이전/다음 날·전체 캘린더 링크와 설치 버튼뿐.
 * 설치 버튼 추적 이름 = `web_content`(자동 콘텐츠 페이지 공통 — 팀장 10/12, 페이지별로 쪼개면 Apple <5 숨김).
 */
/**
 * TRAD-3726 — 메타만 따로 둔다: 색인 여부가 그날 건수(daily-counts)에 달렸는데, 쿼리 훅은 날짜가 유효할 때만
 * 불러야 한다(무효 날짜는 위에서 NotFound 로 먼저 빠진다 — 훅을 조건부로 부르지 않기 위해 컴포넌트를 나눈다).
 */
const DayPageMeta = ({ date, filter, heading }: { date: string; filter: CalendarFilter; heading: string }) => {
  const { data: counts } = useFinancialCalendarDailyCountsQuery(filter);
  return (
    <PageMeta
      title={t('financial_calendar.day.meta_title', { date: heading })}
      description={t('financial_calendar.day.meta_description', { date: heading })}
      path={calendarDayPath(date)}
      noIndex={!isCalendarDayIndexable(counts, date)}
      breadcrumb={[
        { name: t('financial_calendar.meta.title'), path: '/financial-calendar' },
        { name: heading, path: calendarDayPath(date) },
      ]}
    />
  );
};

const FinancialCalendarDayPage = () => {
  const { date } = useParams<{ date: string }>();
  const [selectedEvent, setSelectedEvent] = useState<FinancialCalendarEvent | null>(null);
  const isClosingRef = useRef(false);

  const valid = isValidCalendarDay(date);
  const filter = useMemo(() => (valid ? calendarDayFilter(date) : null), [valid, date]);

  const handleEventSelect = useCallback((event: FinancialCalendarEvent) => {
    if (isClosingRef.current) return;
    setSelectedEvent(event);
  }, []);
  const handleDetailClose = useCallback(() => {
    isClosingRef.current = true;
    setSelectedEvent(null);
    setTimeout(() => {
      isClosingRef.current = false;
    }, 200);
  }, []);

  if (!valid || !filter) return <NotFound />;

  const heading = formatEventDateHeading(date);
  const prev = shiftCalendarDay(date, -1);
  const next = shiftCalendarDay(date, 1);

  return (
    <>
      <DayPageMeta date={date} filter={filter} heading={heading} />
      <PageShell width="data" padding="comfortable">
        {/* 상세 화면 뒤로가기(TRAD-3177 D-6 — 다른 상세와 같은 DetailBackButton · 라벨 = 목적지 이름 키 재사용). */}
        <DetailBackButton to="/financial-calendar" icon="chevron" label={t('financial_calendar.meta.title')} />
        <h1 className="text-xl font-bold mb-4">{t('financial_calendar.day.meta_title', { date: heading })}</h1>

        <nav className="flex flex-wrap items-center gap-3 mb-4 text-sm" aria-label={t('financial_calendar.meta.title')}>
          <Link to={calendarDayPath(prev)} className="inline-flex items-center gap-1 hover:underline">
            <ChevronLeft className="size-4" aria-hidden="true" />
            {t('financial_calendar.day.prev')}
          </Link>
          <Link to={calendarDayPath(next)} className="inline-flex items-center gap-1 hover:underline">
            {t('financial_calendar.day.next')}
            <ChevronRight className="size-4" aria-hidden="true" />
          </Link>
        </nav>

        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-2">
            {/* TRAD-3725 — 일자 페이지의 «필터 없음» = 전체 중요도 · 0건 문구 = «이 날에는 일정이 없습니다»(필터 초기화 버튼 없음). */}
            <EventList
              filter={filter}
              selectedEventId={selectedEvent?.id ?? null}
              onEventSelect={handleEventSelect}
              pristineImpacts={CALENDAR_DAY_PRISTINE_IMPACTS}
              emptyPeriodTitleKey="financial_calendar.empty.day"
            />
          </div>
          <div className="hidden md:block md:col-span-1 border-l border-border pl-4">
            <EventDetailPanel event={selectedEvent} />
          </div>
        </div>

        <CalendarDisclaimer />
      </PageShell>

      <div className="md:hidden">
        <EventDetailDrawer event={selectedEvent} onClose={handleDetailClose} />
      </div>
    </>
  );
};

export default FinancialCalendarDayPage;
